import { useId, useRef, useState } from 'react';

import {
  ANALYTICS_EVENTS,
  track,
  type TrackLocation,
} from '@/lib/analytics';
import { cn } from '@/lib/utils';

const TOPICS = [
  { value: 'software-entwicklung', label: 'Software-Entwicklung' },
  { value: 'app-entwicklung', label: 'App-Entwicklung' },
  { value: 'prozessoptimierung', label: 'Prozessoptimierung' },
  { value: 'corporate-design', label: 'Corporate Design' },
  { value: 'sonstiges', label: 'Sonstiges' },
] as const;

type Status = 'idle' | 'submitting' | 'success' | 'error';

/**
 * The server refuses a token younger than three seconds, so a replacement
 * fetched mid-submission has to age before it is worth sending. A shade over
 * the limit, to stay clear of the clock drift between two machines.
 */
const TOKEN_WARMUP_MS = 3500;

type ContactFormProps = {
  /** Reported as a property on every event, so one funnel can be split by placement. */
  location: TrackLocation;
  className?: string;
};

const FIELD_CLASS =
  'w-full rounded-xl border border-white/10 bg-zinc-900/80 px-4 py-3 text-white placeholder:text-gray-500 transition-colors duration-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/40 focus:outline-none disabled:opacity-60';

const LABEL_CLASS = 'mb-2 block text-sm font-medium text-gray-300';

export function ContactForm({ location, className }: ContactFormProps) {
  const id = useId();
  const [status, setStatus] = useState<Status>('idle');
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  // The server signs a token and remembers when it handed it out, so how long
  // the form took to fill is its own measurement rather than a number this
  // page claims. Fetched on first focus: a visitor who only scrolls past never
  // triggers the request, and the clock starts when they begin typing.
  const token = useRef<string | null>(null);
  const startTracked = useRef(false);
  const retried = useRef(false);

  const fetchToken = async () => {
    try {
      const response = await fetch('/api/contact/token');
      const data = await response.json();
      token.current = typeof data?.token === 'string' ? data.token : null;
    } catch {
      // Leave it null - submitting then fails recoverably rather than here,
      // where the visitor has not asked for anything yet.
      token.current = null;
    }
  };

  const handleFirstFocus = () => {
    if (startTracked.current) return;
    startTracked.current = true;
    track(ANALYTICS_EVENTS.contactStart, { location });
    void fetchToken();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const data = new FormData(form);
    const topic = String(data.get('topic') ?? 'sonstiges');

    setStatus('submitting');
    setErrors([]);
    setFeedback('');
    track(ANALYTICS_EVENTS.contactSubmit, { location, topic });

    const send = () =>
      fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'),
          email: data.get('email'),
          company: data.get('company'),
          phone: data.get('phone'),
          topic,
          message: data.get('message'),
          privacy: data.get('privacy') === 'on',
          zusatz: data.get('zusatz'),
          token: token.current,
        }),
      });

    try {
      let response = await send();
      let result = await response.json().catch(() => null);

      // The token was never fetched, or it outlived the secret that signed it
      // - a tab left open across a deploy. Fetch a fresh one and send again.
      // A brand new token is younger than the minimum fill time, so wait it
      // out first: the visitor sees a slightly longer spinner instead of an
      // error about something they did not do.
      if (result?.code === 'token' && !retried.current) {
        retried.current = true;
        await fetchToken();
        await new Promise((resolve) => setTimeout(resolve, TOKEN_WARMUP_MS));
        response = await send();
        result = await response.json().catch(() => null);
      }

      if (!response.ok || !result?.success) {
        setStatus('error');
        setErrors(Array.isArray(result?.errors) ? result.errors : []);
        setFeedback(
          result?.message ??
            'Der Versand hat leider nicht geklappt. Bitte schreiben Sie uns an info@harmoniemedia.de.',
        );
        track(ANALYTICS_EVENTS.contactError, {
          location,
          topic,
          reason: response.status === 400 ? 'validation' : `http-${response.status}`,
        });
        return;
      }

      setStatus('success');
      setFeedback(result.message);
      form.reset();
      // Each token is good for one submission, so a second enquiry from the
      // same open page needs its own.
      token.current = null;
      startTracked.current = false;
      retried.current = false;
      track(ANALYTICS_EVENTS.contactSuccess, { location, topic });
    } catch {
      setStatus('error');
      setFeedback(
        'Keine Verbindung zum Server. Bitte versuchen Sie es erneut oder schreiben Sie uns an info@harmoniemedia.de.',
      );
      track(ANALYTICS_EVENTS.contactError, { location, topic, reason: 'network' });
    }
  };

  const busy = status === 'submitting';

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={handleFirstFocus}
      noValidate
      className={cn('text-left', className)}
    >
      {/*
        Honeypot: off-screen rather than display:none, which some bots skip.

        The name matters as much as the hiding. This field was called
        "website" and Chrome autofilled it, which flagged a real enquiry as a
        bot and dropped it in silence - `autocomplete="off"` is advice a
        browser is free to ignore, and it does. So the name has to mean
        nothing to an autofill heuristic (those match English tokens like
        website, url, email, phone), while still looking worth filling to a
        script that fills everything it finds. The data- attributes ask the
        common password managers to keep out as well.
      */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={`${id}-zusatz`}>Dieses Feld bitte leer lassen</label>
        <input
          id={`${id}-zusatz`}
          name="zusatz"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          data-1p-ignore=""
          data-lpignore="true"
          data-form-type="other"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className={LABEL_CLASS} htmlFor={`${id}-name`}>
            Name <span className="text-purple-400">*</span>
          </label>
          <input
            id={`${id}-name`}
            name="name"
            type="text"
            required
            maxLength={120}
            autoComplete="name"
            disabled={busy}
            placeholder="Max Mustermann"
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor={`${id}-email`}>
            E-Mail <span className="text-purple-400">*</span>
          </label>
          <input
            id={`${id}-email`}
            name="email"
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            disabled={busy}
            placeholder="max@unternehmen.de"
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor={`${id}-company`}>
            Unternehmen
          </label>
          <input
            id={`${id}-company`}
            name="company"
            type="text"
            maxLength={120}
            autoComplete="organization"
            disabled={busy}
            placeholder="Musterfirma GmbH"
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor={`${id}-phone`}>
            Telefon
          </label>
          <input
            id={`${id}-phone`}
            name="phone"
            type="tel"
            maxLength={60}
            autoComplete="tel"
            disabled={busy}
            placeholder="06162 9899176"
            className={FIELD_CLASS}
          />
        </div>
      </div>

      <div className="mt-5">
        <label className={LABEL_CLASS} htmlFor={`${id}-topic`}>
          Worum geht es?
        </label>
        <select
          id={`${id}-topic`}
          name="topic"
          defaultValue="software-entwicklung"
          disabled={busy}
          className={cn(FIELD_CLASS, 'appearance-none')}
        >
          {TOPICS.map((topic) => (
            <option key={topic.value} value={topic.value} className="bg-zinc-900">
              {topic.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label className={LABEL_CLASS} htmlFor={`${id}-message`}>
          Nachricht <span className="text-purple-400">*</span>
        </label>
        <textarea
          id={`${id}-message`}
          name="message"
          required
          rows={6}
          minLength={10}
          maxLength={5000}
          disabled={busy}
          placeholder="Erzählen Sie uns kurz, was Sie vorhaben."
          className={cn(FIELD_CLASS, 'resize-y')}
        />
      </div>

      <div className="mt-5 flex items-start gap-3">
        <input
          id={`${id}-privacy`}
          name="privacy"
          type="checkbox"
          required
          disabled={busy}
          className="mt-1 h-4 w-4 shrink-0 rounded border-white/20 bg-zinc-900 accent-purple-600"
        />
        <label htmlFor={`${id}-privacy`} className="text-sm leading-relaxed text-gray-400">
          Ich habe die{' '}
          <a
            href="/datenschutz"
            className="text-purple-400 underline underline-offset-2 hover:text-purple-300"
          >
            Datenschutzerklärung
          </a>{' '}
          gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung
          meiner Anfrage zu. <span className="text-purple-400">*</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={busy}
        className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-purple-600 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {busy ? (
          <>
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            Wird gesendet …
          </>
        ) : (
          <>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Nachricht senden
          </>
        )}
      </button>

      <div aria-live="polite" className="mt-6 empty:mt-0">
        {feedback && (
          <div
            className={cn(
              'rounded-xl border p-4 text-sm',
              status === 'success'
                ? 'border-green-500/30 bg-green-500/10 text-green-300'
                : 'border-red-500/30 bg-red-500/10 text-red-300',
            )}
          >
            <p>{feedback}</p>
            {errors.length > 0 && (
              <ul className="mt-2 list-inside list-disc space-y-1">
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </form>
  );
}
